/**
 * Keeps the verdict steady while the camera keeps reading.
 *
 * The camera sees codes many times a second, and while she lifts a pot it may briefly
 * catch a neighbouring sticker. Rules:
 * - the first code seen is accepted immediately (fast first verdict);
 * - the current code being seen again changes nothing;
 * - a DIFFERENT code replaces the current one only after it has been seen continuously
 *   for `holdMs` (default 600 ms), so a passing glimpse never flips the verdict.
 *
 * Pure: the caller supplies the time, so it is testable without a camera or a clock.
 */

export interface Stabilizer {
  /** Feed one decoded frame. Returns the code to show if it changed, otherwise null. */
  feed(code: string, nowMs: number): string | null;
  /** Forget the current code (after an action), so the next scan is accepted immediately. */
  reset(): void;
}

export function createStabilizer(holdMs = 600): Stabilizer {
  let current: string | null = null;
  let candidate: { code: string; since: number } | null = null;

  return {
    feed(code, nowMs) {
      if (current === null) {
        current = code;
        candidate = null;
        return code;
      }
      if (code === current) {
        candidate = null;
        return null;
      }
      if (!candidate || candidate.code !== code) {
        candidate = { code, since: nowMs };
        return null;
      }
      if (nowMs - candidate.since >= holdMs) {
        current = code;
        candidate = null;
        return code;
      }
      return null;
    },
    reset() {
      current = null;
      candidate = null;
    },
  };
}
